import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchMatchupDetail, MatchupDetailData } from '../api/matchups';
import LayoutWithChat from "./LayoutWithChat";
import ResponsiveTable, { Column } from './ResponsiveTable';

interface Player {
  id: string;
  position: string;
  name: string;
  team: string;
  opponent: string;
  projection: number;
  actual: number;
  gameStatus?: string;
  gameTime?: string;
}

interface Team {
  id: string;
  name: string;
  logo?: string;
  record: string;
  score: number;
}

interface MatchupProps {
  homeTeam?: Team;
  awayTeam?: Team;
  homeRoster?: Player[];
  awayRoster?: Player[];
}

const Matchup: React.FC<MatchupProps> = ({
  homeTeam = {
    id: "1",
    name: "The Underdogs",
    record: "8-2",
    score: 105.5,
  },
  awayTeam = {
    id: "2",
    name: "Gridiron Gladiators",
    record: "7-3",
    score: 88.25,
  },
  homeRoster = [
    // Starters
    {
      id: "1",
      position: "QB",
      name: "J. Allen",
      team: "BUF",
      opponent: "vs. MIA",
      projection: 22.5,
      actual: 25.5,
      gameStatus: "Live",
      gameTime: "Q2 12:34",
    },
    {
      id: "2",
      position: "RB",
      name: "C. McCaffrey",
      team: "SF",
      opponent: "@ SEA",
      projection: 18.2,
      actual: 21.0,
      gameStatus: "Live",
      gameTime: "Q3 8:45",
    },
    {
      id: "3",
      position: "RB",
      name: "D. Henry",
      team: "TEN",
      opponent: "vs. HOU",
      projection: 15.7,
      actual: 19.2,
      gameStatus: "Final",
    },
    {
      id: "4",
      position: "WR",
      name: "T. Hill",
      team: "MIA",
      opponent: "@ BUF",
      projection: 20.5,
      actual: 28.0,
      gameStatus: "Live",
      gameTime: "Q2 12:34",
    },
    {
      id: "5",
      position: "WR",
      name: "S. Diggs",
      team: "BUF",
      opponent: "vs. MIA",
      projection: 17.8,
      actual: 22.1,
      gameStatus: "Live",
      gameTime: "Q2 12:34",
    },
    {
      id: "6",
      position: "TE",
      name: "T. Kelce",
      team: "KC",
      opponent: "@ LV",
      projection: 15.0,
      actual: 17.5,
      gameStatus: "Final",
    },
    {
      id: "7",
      position: "FLEX",
      name: "A. Jones",
      team: "GB",
      opponent: "@ CHI",
      projection: 14.2,
      actual: 16.8,
      gameStatus: "4:25 PM",
    },
    {
      id: "8",
      position: "FLEX",
      name: "C. Olave",
      team: "NO",
      opponent: "vs. CAR",
      projection: 13.5,
      actual: 0,
      gameStatus: "1:00 PM",
    },
    {
      id: "9",
      position: "DST",
      name: "Buffalo Bills",
      team: "BUF",
      opponent: "vs. MIA",
      projection: 8.5,
      actual: 11.0,
      gameStatus: "Live",
      gameTime: "Q2 12:34",
    },
    {
      id: "10",
      position: "K",
      name: "J. Tucker",
      team: "BAL",
      opponent: "@ LAC",
      projection: 9.0,
      actual: 12.0,
      gameStatus: "Live",
      gameTime: "Q4 2:15",
    },
    // Bench
    {
      id: "11",
      position: "BENCH",
      name: "J. Burrow",
      team: "CIN",
      opponent: "BYE",
      projection: 0,
      actual: 0,
      gameStatus: "BYE",
    },
    {
      id: "12",
      position: "BENCH",
      name: "N. Harris",
      team: "PIT",
      opponent: "vs. CLE",
      projection: 11.2,
      actual: 8.5,
      gameStatus: "Final",
    },
    {
      id: "13",
      position: "BENCH",
      name: "G. Wilson",
      team: "NYJ",
      opponent: "@ BUF",
      projection: 10.8,
      actual: 0,
      gameStatus: "1:00 PM",
    },
    {
      id: "14",
      position: "BENCH",
      name: "G. Kittle",
      team: "SF",
      opponent: "@ SEA",
      projection: 9.8,
      actual: 14.2,
      gameStatus: "Live",
      gameTime: "Q3 8:45",
    },
  ],
  awayRoster = [
    // Starters
    {
      id: "15",
      position: "QB",
      name: "P. Mahomes",
      team: "KC",
      opponent: "@ LV",
      projection: 20.1,
      actual: 18.75,
      gameStatus: "Final",
    },
    {
      id: "16",
      position: "RB",
      name: "A. Ekeler",
      team: "LAC",
      opponent: "vs. BAL",
      projection: 16.8,
      actual: 15.5,
      gameStatus: "Live",
      gameTime: "Q4 2:15",
    },
    {
      id: "17",
      position: "RB",
      name: "J. Jacobs",
      team: "LV",
      opponent: "vs. KC",
      projection: 14.5,
      actual: 12.3,
      gameStatus: "Final",
    },
    {
      id: "18",
      position: "WR",
      name: "J. Jefferson",
      team: "MIN",
      opponent: "vs. CHI",
      projection: 15.2,
      actual: 0,
      gameStatus: "1:00 PM",
    },
    {
      id: "19",
      position: "WR",
      name: "C. Lamb",
      team: "DAL",
      opponent: "@ PHI",
      projection: 16.9,
      actual: 0,
      gameStatus: "8:20 PM",
    },
    {
      id: "20",
      position: "TE",
      name: "M. Andrews",
      team: "BAL",
      opponent: "@ LAC",
      projection: 10.5,
      actual: 8.5,
      gameStatus: "Live",
      gameTime: "Q4 2:15",
    },
    {
      id: "21",
      position: "FLEX",
      name: "T. Etienne",
      team: "JAX",
      opponent: "vs. IND",
      projection: 13.8,
      actual: 16.4,
      gameStatus: "Final",
    },
    {
      id: "22",
      position: "FLEX",
      name: "B. Aiyuk",
      team: "SF",
      opponent: "@ SEA",
      projection: 12.2,
      actual: 14.1,
      gameStatus: "Live",
      gameTime: "Q3 8:45",
    },
    {
      id: "23",
      position: "DST",
      name: "San Francisco 49ers",
      team: "SF",
      opponent: "@ SEA",
      projection: 7.0,
      actual: 9.0,
      gameStatus: "Live",
      gameTime: "Q3 8:45",
    },
    {
      id: "24",
      position: "K",
      name: "D. Carlson",
      team: "LV",
      opponent: "vs. KC",
      projection: 8.5,
      actual: 7.0,
      gameStatus: "Final",
    },
    // Bench
    {
      id: "25",
      position: "BENCH",
      name: "T. Lawrence",
      team: "JAX",
      opponent: "vs. IND",
      projection: 16.5,
      actual: 19.8,
      gameStatus: "Final",
    },
    {
      id: "26",
      position: "BENCH",
      name: "R. Stevenson",
      team: "NE",
      opponent: "@ NYG",
      projection: 12.8,
      actual: 10.2,
      gameStatus: "Final",
    },
    {
      id: "27",
      position: "BENCH",
      name: "T. Higgins",
      team: "CIN",
      opponent: "BYE",
      projection: 0,
      actual: 0,
      gameStatus: "BYE",
    },
    {
      id: "28",
      position: "BENCH",
      name: "D. Waller",
      team: "NYG",
      opponent: "vs. NE",
      projection: 8.2,
      actual: 6.5,
      gameStatus: "Final",
    },
  ]
}) => {
  const navigate = useNavigate();
  const { leagueId, matchupId } = useParams<{ leagueId: string; matchupId: string }>();
  const [searchParams] = useSearchParams();
  const week = searchParams.get('week');
  
  const [matchupDetail, setMatchupDetail] = useState<MatchupDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Effect to fetch matchup data when component mounts
  useEffect(() => {
    if (leagueId && matchupId && week) {
      loadMatchupData();
    }
  }, [leagueId, matchupId, week]);

  const loadMatchupData = async () => {
    if (!leagueId || !matchupId || !week) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const data = await fetchMatchupDetail(leagueId, matchupId, week);
      setMatchupDetail(data);
    } catch (err: any) {
      if (err.message === 'Authentication expired') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to load matchup details');
        console.error('Error loading matchup details:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };



  const handlePlayerClick = (playerName: string) => {
    navigate(`/player/${encodeURIComponent(playerName)}`);
  };

  const getInitials = (name: string): string => {
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };



  // Single column definition for both tables
  const createMatchupColumns = (): Column[] => [
    {
      key: 'homePosition',
      label: 'POS',
      width: '8%',
      className: 'text-sm font-semibold text-text-primary',
      render: (value, data) => data.position
    },
    {
      key: 'homePlayer',
      label: matchupDetail ? matchupDetail.team1.name : homeTeam.name,
      width: '30%',
      className: 'text-sm',
      render: (value, data) => (
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => handlePlayerClick(data.homePlayer.name)}
        >
          {data.homePlayer.imageUrl ? (
            <img
              src={data.homePlayer.imageUrl}
              alt={data.homePlayer.name}
              className="h-8 w-8 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium text-text-secondary">
              {getInitials(data.homePlayer.name)}
            </div>
          )}
          <div>
            <span className="text-sm font-medium text-text-primary">
              {data.homePlayer.name}
            </span>
            <div className="text-xs text-text-secondary uppercase">
              {data.homePlayer.actualPosition || data.homePlayer.position} • {data.homePlayer.team}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'homeActual',
      label: 'Actual',
      width: '12%',
      className: 'text-sm font-medium text-text-primary text-right',
      render: (value, data) => data.homePlayer.actual || '--'
    },
    {
      key: 'awayActual',
      label: 'Actual',
      width: '12%',
      className: 'text-sm font-medium text-text-primary text-left',
      render: (value, data) => data.awayPlayer?.actual || '--'
    },
    {
      key: 'awayPlayer',
      label: matchupDetail ? matchupDetail.team2.name : awayTeam.name,
      width: '30%',
      className: 'text-sm text-right',
      render: (value, data) => data.awayPlayer ? (
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity justify-end"
          onClick={() => handlePlayerClick(data.awayPlayer.name)}
        >
          <div className="text-right">
            <span className="text-sm font-medium text-text-primary">
              {data.awayPlayer.name}
            </span>
            <div className="text-xs text-text-secondary uppercase">
              {data.awayPlayer.actualPosition || data.awayPlayer.position} • {data.awayPlayer.team}
            </div>
          </div>
          {data.awayPlayer.imageUrl ? (
            <img
              src={data.awayPlayer.imageUrl}
              alt={data.awayPlayer.name}
              className="h-8 w-8 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium text-text-secondary">
              {getInitials(data.awayPlayer.name)}
            </div>
          )}
        </div>
      ) : null
    },
    {
      key: 'awayPosition',
      label: 'POS',
      width: '8%',
      className: 'text-sm font-semibold text-text-primary text-right',
      render: (value, data) => data.awayPlayer?.position || ''
    }
  ];

  // Column definitions for bench table


  return (
    <LayoutWithChat 
    >
      {isLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-text-secondary">Loading matchup details...</div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="text-red-400">{error}</div>
          <button
            onClick={loadMatchupData}
            className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary text-sm font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : (
        <>
          {/* Matchup Header */}
            <div className="bg-bg-secondary border border-border-primary rounded-lg">
              <div className="p-6">


                {/* Score Display */}
                <div className="flex justify-between items-center gap-2 sm:gap-4">
                  <div className="flex flex-col items-center flex-1 min-w-0">
                    {(matchupDetail?.team1.logo || homeTeam.logo) ? (
                      <img
                        src={matchupDetail?.team1.logo || homeTeam.logo}
                        alt={matchupDetail?.team1.name || homeTeam.name}
                        className="h-12 w-12 sm:h-20 sm:w-20 mb-2 sm:mb-3 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 sm:h-20 sm:w-20 mb-2 sm:mb-3 rounded-full bg-bg-tertiary flex items-center justify-center text-sm sm:text-2xl font-medium text-text-secondary">
                        {(matchupDetail?.team1.name || homeTeam.name).substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <h2 className="text-sm sm:text-xl font-normal font-primary text-text-primary text-center w-full px-1">
                      {matchupDetail ? matchupDetail.team1.name : homeTeam.name}
                    </h2>
                    <p className="text-xs sm:text-sm text-text-secondary">
                      {homeTeam.record}
                    </p>
                  </div>

                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center justify-center gap-2 sm:gap-4">
                      <span className="text-2xl sm:text-5xl font-light text-text-primary">
                        {matchupDetail ? matchupDetail.team1.totalScore.toFixed(1) : homeTeam.score}
                      </span>
                      <span className="text-xl sm:text-3xl font-light text-text-secondary">
                        -
                      </span>
                      <span className="text-2xl sm:text-5xl font-light text-text-primary">
                        {matchupDetail ? matchupDetail.team2.totalScore.toFixed(1) : awayTeam.score}
                      </span>
                    </div>
                    {matchupDetail && (matchupDetail.team1.projected || matchupDetail.team2.projected) && (
                      <div className="flex items-center justify-center gap-2 sm:gap-4">
                        <span className="text-sm sm:text-lg font-light text-text-secondary">
                          {matchupDetail.team1.projected?.toFixed(1) || '0.0'}
                        </span>
                        <span className="text-sm sm:text-lg font-light text-text-secondary">
                          -
                        </span>
                        <span className="text-sm sm:text-lg font-light text-text-secondary">
                          {matchupDetail.team2.projected?.toFixed(1) || '0.0'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-center flex-1 min-w-0">
                    {(matchupDetail?.team2.logo || awayTeam.logo) ? (
                      <img
                        src={matchupDetail?.team2.logo || awayTeam.logo}
                        alt={matchupDetail?.team2.name || awayTeam.name}
                        className="h-12 w-12 sm:h-20 sm:w-20 mb-2 sm:mb-3 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 sm:h-20 sm:w-20 mb-2 sm:mb-3 rounded-full bg-bg-tertiary flex items-center justify-center text-sm sm:text-2xl font-medium text-text-secondary">
                        {(matchupDetail?.team2.name || awayTeam.name).substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <h2 className="text-sm sm:text-xl font-normal font-primary text-text-primary text-center w-full px-1">
                      {matchupDetail ? matchupDetail.team2.name : awayTeam.name}
                    </h2>
                    <p className="text-xs sm:text-sm text-text-secondary">
                      {awayTeam.record}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Starters */}
            <div className="flex flex-col gap-4">
              <h2 className="text-xl font-normal font-primary text-text-primary">
                Starters
              </h2>
              <ResponsiveTable
                columns={createMatchupColumns()}
                data={(matchupDetail ? matchupDetail.team1.starters.map(player => ({
                  id: player.id,
                  position: player.lineupPosition || player.position,
                  actualPosition: player.position,
                  name: player.name,
                  team: player.team,
                  opponent: "vs OPP",
                  projection: player.projected || 0,
                  actual: player.weekPoints || 0,
                  gameStatus: "Final",
                  gameTime: "1:00 PM",
                  imageUrl: player.imageUrl
                })) : homeRoster.filter((p) => p.position !== "BENCH"))
                  .map((homePlayer, index) => {
                    const awayStarters = matchupDetail ? matchupDetail.team2.starters.map(player => ({
                      id: player.id,
                      position: player.lineupPosition || player.position,
                      actualPosition: player.position,
                      name: player.name,
                      team: player.team,
                      opponent: "vs OPP",
                      projection: player.projected || 0,
                      actual: player.weekPoints || 0,
                      gameStatus: "Final",
                      gameTime: "1:00 PM",
                      imageUrl: player.imageUrl
                    })) : awayRoster.filter((p) => p.position !== "BENCH");
                    const awayPlayer = awayStarters[index];
                    return {
                      id: homePlayer.id,
                      position: homePlayer.position,
                      homePlayer: homePlayer,
                      awayPlayer: awayPlayer,
                      homeActual: homePlayer.actual,
                      awayActual: awayPlayer?.actual
                    };
                  })}
                keyExtractor={(item) => item.id}
                cardRenderer={(item) => (
                  <div className="bg-bg-secondary border border-border-primary rounded-lg p-2">
                    {/* Position Header */}
                    <div className="text-center mb-2">
                      <div className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
                        {item.homePlayer.actualPosition ? item.position : ''}
                      </div>
                    </div>
                    
                    {/* Players Side by Side */}
                    <div className="flex items-start justify-between">
                      {/* Home Player */}
                      <div 
                        className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity flex-1"
                        onClick={() => handlePlayerClick(item.homePlayer.name)}
                      >
                        {item.homePlayer.imageUrl ? (
                          <img
                            src={item.homePlayer.imageUrl}
                            alt={item.homePlayer.name}
                            className="h-12 w-12 rounded-full object-cover mb-2"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-bg-tertiary text-sm font-medium text-text-secondary mb-2">
                            {getInitials(item.homePlayer.name)}
                          </div>
                        )}
                        <div className="text-center">
                          <div className="text-sm font-medium text-text-primary mb-1">
                            {item.homePlayer.name.split(' ').map((name: string) => name.length > 8 ? name.substring(0, 8) + '.' : name).join(' ')}
                          </div>
                          <div className="text-xs text-text-secondary uppercase mb-2">
                            {item.homePlayer.actualPosition || item.homePlayer.position} • {item.homePlayer.team}
                          </div>
                          <div className="text-lg font-bold text-text-primary">
                            {item.homePlayer.actual || '--'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Away Player */}
                      {item.awayPlayer ? (
                        <div 
                          className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity flex-1"
                          onClick={() => handlePlayerClick(item.awayPlayer.name)}
                        >
                          {item.awayPlayer.imageUrl ? (
                            <img
                              src={item.awayPlayer.imageUrl}
                              alt={item.awayPlayer.name}
                              className="h-12 w-12 rounded-full object-cover mb-2"
                            />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-bg-tertiary text-sm font-medium text-text-secondary mb-2">
                              {getInitials(item.awayPlayer.name)}
                            </div>
                          )}
                          <div className="text-center">
                            <div className="text-sm font-medium text-text-primary mb-1">
                              {item.awayPlayer.name.split(' ').map((name: string) => name.length > 8 ? name.substring(0, 8) + '.' : name).join(' ')}
                            </div>
                            <div className="text-xs text-text-secondary uppercase mb-2">
                              {item.awayPlayer.actualPosition || item.awayPlayer.position} • {item.awayPlayer.team}
                            </div>
                            <div className="text-lg font-bold text-text-primary">
                              {item.awayPlayer.actual || '--'}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1"></div>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Bench */}
            <div className="flex flex-col gap-4">
              <h2 className="text-xl font-normal font-primary text-text-primary">
                Bench
              </h2>
              <ResponsiveTable
                columns={createMatchupColumns()}
                data={(matchupDetail ? matchupDetail.team1.bench.map(player => ({
                  id: player.id,
                  position: player.position,
                  name: player.name,
                  team: player.team,
                  opponent: "vs OPP",
                  projection: player.projected || 0,
                  actual: player.weekPoints || 0,
                  gameStatus: "Final",
                  gameTime: "1:00 PM",
                  imageUrl: player.imageUrl
                })) : homeRoster.filter((p) => p.position === "BENCH"))
                  .map((homePlayer, index) => {
                    const awayBench = matchupDetail ? matchupDetail.team2.bench.map(player => ({
                      id: player.id,
                      position: player.position,
                      name: player.name,
                      team: player.team,
                      opponent: "vs OPP",
                      projection: player.projected || 0,
                      actual: player.weekPoints || 0,
                      gameStatus: "Final",
                      gameTime: "1:00 PM",
                      imageUrl: player.imageUrl
                    })) : awayRoster.filter((p) => p.position === "BENCH");
                    const awayPlayer = awayBench[index];
                    return {
                      id: homePlayer.id,
                      position: homePlayer.position,
                      homePlayer: homePlayer,
                      awayPlayer: awayPlayer,
                      homeActual: homePlayer.actual,
                      awayActual: awayPlayer?.actual
                    };
                  })}
                keyExtractor={(item) => item.id}
                cardRenderer={(item) => (
                  <div className="bg-bg-secondary border border-border-primary rounded-lg p-2">
                    {/* Position Header */}
                    <div className="text-center mb-2">
                      <div className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
                        {item.homePlayer.actualPosition ? item.position : ''}
                      </div>
                    </div>
                    
                    {/* Players Side by Side */}
                    <div className="flex items-start justify-between">
                      {/* Home Player */}
                      <div 
                        className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity flex-1"
                        onClick={() => handlePlayerClick(item.homePlayer.name)}
                      >
                        {item.homePlayer.imageUrl ? (
                          <img
                            src={item.homePlayer.imageUrl}
                            alt={item.homePlayer.name}
                            className="h-12 w-12 rounded-full object-cover mb-2"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-bg-tertiary text-sm font-medium text-text-secondary mb-2">
                            {getInitials(item.homePlayer.name)}
                          </div>
                        )}
                        <div className="text-center">
                          <div className="text-sm font-medium text-text-primary mb-1">
                            {item.homePlayer.name.split(' ').map((name: string) => name.length > 8 ? name.substring(0, 8) + '.' : name).join(' ')}
                          </div>
                          <div className="text-xs text-text-secondary uppercase mb-2">
                            {item.homePlayer.actualPosition || item.homePlayer.position} • {item.homePlayer.team}
                          </div>
                          <div className="text-lg font-bold text-text-primary">
                            {item.homePlayer.actual || '--'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Away Player */}
                      {item.awayPlayer ? (
                        <div 
                          className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity flex-1"
                          onClick={() => handlePlayerClick(item.awayPlayer.name)}
                        >
                          {item.awayPlayer.imageUrl ? (
                            <img
                              src={item.awayPlayer.imageUrl}
                              alt={item.awayPlayer.name}
                              className="h-12 w-12 rounded-full object-cover mb-2"
                            />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-bg-tertiary text-sm font-medium text-text-secondary mb-2">
                              {getInitials(item.awayPlayer.name)}
                            </div>
                          )}
                          <div className="text-center">
                            <div className="text-sm font-medium text-text-primary mb-1">
                              {item.awayPlayer.name.split(' ').map((name: string) => name.length > 8 ? name.substring(0, 8) + '.' : name).join(' ')}
                            </div>
                            <div className="text-xs text-text-secondary uppercase mb-2">
                              {item.awayPlayer.actualPosition || item.awayPlayer.position} • {item.awayPlayer.team}
                            </div>
                            <div className="text-lg font-bold text-text-primary">
                              {item.awayPlayer.actual || '--'}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1"></div>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
        </>
      )}
    </LayoutWithChat>
  );
};

export default Matchup;
